// ─── useGeneration ────────────────────────────────────────────────────────
// Connects to backend API for content generation.
// Uses status polling to track real pipeline progress via phase groups.

import { useRef, useCallback } from 'react';
import i18n from '../i18n/index.js';
import { getOutputLanguage } from '../i18n/locale.js';
import { buildBriefRequest, startPipelineRun, getPipelineRunStatus, getPipelineRunContent, cancelPipelineRun } from '../services/api/backendClient.js';
import { toneStateFromParams } from '../utils/toneMigration.js';
import { refusalFromError, describeRefusal } from '../utils/runRefusal.js';
import { resultsFromRunContent } from '../utils/runResults.js';
import { refreshEntitlements } from './useEntitlements.js';
import { refreshOutputEntitlements } from './outputEntitlementsStore.js';
import { getModeConfig } from '../constants/modes.js';
import { buildPipelineSlots, mapPipelineStatus } from '../constants/pipelineSlots.js';

const POLL_INTERVAL_MS = 3000;

// A DISABLED persona is refused at run time too (422 BRIEF_VALIDATION_FAILED,
// `field: "persona"`), not just filtered out of the picker. That can only happen
// when this tab's persona list is older than the tenant's toggles —
// useContextOptions fetches once per language and never revalidates, so an admin
// switching a persona off leaves every open Content Generator offering it.
//
// ⛔ NOT routed through runRefusal.js: that module is the ENTITLEMENT taxonomy
// ("a verdict about what this workspace may do"), and this is a stale-client
// validation failure with a completely different remedy. The server's message is
// English and describes a schema violation; what the user needs is "reload".
function personaUnavailableMessage(err) {
  if (err?.code !== 'BRIEF_VALIDATION_FAILED') return '';
  const errors = Array.isArray(err.details?.errors) ? err.details.errors : [];
  return errors.some(e => e?.field === 'persona')
    ? i18n.t('errors:generation.personaUnavailable')
    : '';
}

export function useGeneration({ dispatch, stateRef }) {
  const isGeneratingRef = useRef(false);
  const generationStartTimeRef = useRef(null);
  const taskIdRef = useRef(null);
  const cancelRequestedRef = useRef(false);   // clicked before task_id existed
  const cancelInFlightRef = useRef(false);    // dedupe double-click
  const cancelStatusRef = useRef('idle');     // 'idle' | 'pending' | 'too-late'

  const setKey = useCallback((key, value) => dispatch({ type: 'SET_KEY', key, value }), [dispatch]);

  const performCancel = useCallback(async (taskId) => {
    if (cancelInFlightRef.current) return;
    cancelInFlightRef.current = true;
    try {
      await cancelPipelineRun(taskId);
      cancelStatusRef.current = 'pending';   // backend accepted; poll will see 'cancelled'
    } catch (e) {
      if (e && e.status === 409) {
        cancelStatusRef.current = 'too-late'; // styling started — run completes normally
      }
      // other failures: leave status 'idle', run continues; non-fatal by design
    } finally {
      cancelInFlightRef.current = false;
    }
  }, []);

  const executeGeneration = useCallback(async (state) => {
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;
    generationStartTimeRef.current = Date.now();
    taskIdRef.current = null;
    cancelRequestedRef.current = false;
    cancelInFlightRef.current = false;
    cancelStatusRef.current = 'idle';

    // Build request and determine which slots are active.
    // Output language comes from the header locale selector (single source of truth).
    const briefRequest = buildBriefRequest(state, getOutputLanguage());
    const slots = buildPipelineSlots({
      researchRequested: !!briefRequest.research_requested,
    });

    // Clear previous output before starting new generation
    dispatch({ type: 'SET_KEY', key: 'outputHistory', value: [] });

    // Initialize stepper state
    const initialTimings = { setup: { startedAt: Date.now() } };
    dispatch({ type: 'SET_BATCH', payload: {
      isLoading: true,
      error: null,
      // Clear the previous verdict alongside the previous error: a run that is
      // starting has, by definition, not been refused yet.
      runRefusal: null,
      pipelineProgress: {
        slots,
        activePhase: { slotId: 'setup', subLabel: i18n.t('outputs:stepper.sub.CREATED') },
        isComplete: false,
        isError: false,
        errorMessage: null,
        slotTimings: initialTimings,
      },
    }});

    // Mutable timings map — updated on each poll, snapshot dispatched
    let timings = { ...initialTimings };
    let lastSlotId = 'setup';

    try {
      // 1. Start pipeline run
      const { task_id } = await startPipelineRun(briefRequest, { tenant: state.tenant });
      taskIdRef.current = task_id;
      if (cancelRequestedRef.current) {
        performCancel(task_id); // fire-and-forget; polling continues to observe 'cancelled'
      }

      // 2. Poll for completion with real status mapping
      const pollResult = await new Promise((resolve, reject) => {
        const poll = async () => {
          try {
            const statusResp = await getPipelineRunStatus(task_id);
            const { status } = statusResp;

            // Terminal states
            if (status === 'completed' || status === 'approved' ||
                status === 'COMPLETED') {
              // Close timing on last active slot
              if (lastSlotId && timings[lastSlotId] && !timings[lastSlotId].endedAt) {
                timings[lastSlotId].endedAt = Date.now();
              }
              // Mark finishing slot
              if (!timings.finishing) {
                timings.finishing = { startedAt: Date.now(), endedAt: Date.now() };
              } else if (!timings.finishing.endedAt) {
                timings.finishing.endedAt = Date.now();
              }
              const warningsCount = statusResp.warnings_count;
              dispatch({ type: 'SET_KEY', key: 'pipelineProgress', value: {
                slots,
                activePhase: null,
                isComplete: true,
                isError: false,
                errorMessage: null,
                slotTimings: { ...timings },
                warningsCount: warningsCount != null && warningsCount > 0 ? warningsCount : null,
              }});
              resolve({ statusResp, outcome: 'completed' });
              return;
            }

            if (status === 'cancelled' || status === 'CANCELLED') {
              if (lastSlotId && timings[lastSlotId] && !timings[lastSlotId].endedAt) {
                timings[lastSlotId].endedAt = Date.now();
              }
              dispatch({ type: 'SET_KEY', key: 'pipelineProgress', value: {
                slots,
                activePhase: lastSlotId ? { slotId: lastSlotId, subLabel: '' } : null,
                isComplete: false,
                isError: false,
                isCancelled: true,
                errorMessage: null,
                slotTimings: { ...timings },
                cancelStatus: 'done',
                nonCancellable: true,
              }});
              resolve({ statusResp, outcome: 'cancelled' });
              return;
            }

            if (status === 'failed' || status === 'FAILED') {
              const displayMessage =
                statusResp.user_message
                ?? statusResp.error_message
                ?? i18n.t('errors:generation.pipelineFailed');
              reject(new Error(displayMessage));
              return;
            }

            // Map status to phase
            const phase = mapPipelineStatus(statusResp);
            if (phase) {
              // Detect slot transition → update timings
              if (phase.slotId !== lastSlotId) {
                const now = Date.now();
                // Close previous slot timing
                if (lastSlotId && timings[lastSlotId] && !timings[lastSlotId].endedAt) {
                  timings[lastSlotId].endedAt = now;
                }
                // Open new slot timing (skip if slot is marked skipped)
                if (!timings[phase.slotId]) {
                  timings[phase.slotId] = { startedAt: now };
                }
                lastSlotId = phase.slotId;
              }

              const cancelPending = cancelStatusRef.current === 'pending';
              dispatch({ type: 'SET_KEY', key: 'pipelineProgress', value: {
                slots,
                activePhase: (cancelPending && phase)
                  ? { ...phase, subLabel: i18n.t('outputs:stepper.cancelling') }
                  : phase,
                isComplete: false,
                isError: false,
                errorMessage: null,
                slotTimings: { ...timings },
                cancelStatus: cancelStatusRef.current === 'idle' ? null : cancelStatusRef.current,
                nonCancellable: status === 'STYLING' || cancelStatusRef.current === 'too-late',
              }});
            }

            setTimeout(poll, POLL_INTERVAL_MS);
          } catch (err) {
            reject(err);
          }
        };
        setTimeout(poll, POLL_INTERVAL_MS);
      });

      if (pollResult.outcome === 'cancelled') {
        const elapsedSec = Math.round((Date.now() - generationStartTimeRef.current) / 1000);
        const cancelledEntry = {
          id: task_id,
          timestamp: new Date(),
          mode: state.activeMode,
          modeConfig: getModeConfig(state.activeMode),
          results: { _taskId: task_id, status: 'cancelled', cancelled: true, content: null },
          params: {
            ...briefRequest,
            activeMode: state.activeMode,
            socialChannel: state.socialChannel,
            emailChannel: state.emailChannel,
          },
          generationDuration: elapsedSec,
        };
        // NOTE: do NOT set wizardStep — stay on step 1 (edge case 7).
        dispatch({ type: 'SET_KEY', key: 'outputHistory',
          value: [cancelledEntry, ...(stateRef.current.outputHistory || [])] });
        return; // finally{} handles isLoading + stepper teardown
      }

      // 3. Fetch content and build history entry
      const [runStatus, runContent] = await Promise.all([
        getPipelineRunStatus(task_id),
        getPipelineRunContent(task_id),
      ]);
      const elapsedSec = Math.round((Date.now() - generationStartTimeRef.current) / 1000);

      const historyEntry = {
        id: task_id,
        timestamp: new Date(),
        mode: state.activeMode,
        modeConfig: getModeConfig(state.activeMode),
        results: resultsFromRunContent(task_id, runContent, runStatus),
        params: {
          ...briefRequest,
          activeMode: state.activeMode,
          socialChannel: state.socialChannel,
          emailChannel: state.emailChannel,
        },
        generationDuration: elapsedSec,
      };

      dispatch({ type: 'SET_BATCH', payload: {
        outputHistory: [historyEntry, ...(stateRef.current.outputHistory || [])],
        wizardStep: 2,
      }});

    } catch (err) {
      // PB4/D10: a refusal is a VERDICT about what this workspace may do, not a
      // pipeline failure — it is branched on `err.code` (never on the message)
      // and rendered with its own copy and CTA, so it must not fall into the
      // generic error modal.
      const refusal = refusalFromError(err);
      const stalePersona = personaUnavailableMessage(err);
      const displayMessage = refusal
        ? describeRefusal(refusal).body
        : (stalePersona || err.message || i18n.t('errors:generation.unknown'));

      // Update stepper to error state
      dispatch({ type: 'SET_KEY', key: 'pipelineProgress', value: {
        slots,
        activePhase: lastSlotId ? { slotId: lastSlotId, subLabel: '' } : null,
        isComplete: false,
        isError: true,
        errorMessage: displayMessage,
        slotTimings: { ...timings },
      }});

      if (refusal) {
        setKey('runRefusal', refusal);

        // The plan's output-type grant changed under us: the wizard offered
        // something the gate refuses (a stale tab, a plan change mid-brief, or a
        // read that fell open). Re-read /entitlements so the locks catch up.
        // ⛔ NOT from `refusal.details.granted` — that list carries PREFIXED ids
        // with the wildcard unexpanded, so rendering or storing it would show
        // `cg:out:linkedin-post`, or a literal `*` on an unlimited plan.
        if (refusal.code === 'FEATURE_NOT_IN_PLAN' || refusal.code === 'APP_NOT_ASSIGNED') {
          refreshOutputEntitlements();
        }

        // ⚠️ A 402 after a green pre-flight is EXPECTED, not a bug: the wallet
        // is the customer's, shared across every workspace it owns, and
        // /balance is cached 60s. Resync the tile — and NEVER auto-retry the
        // run, which would spend a second attempt on the same verdict.
        if (refusal.status === 402) refreshEntitlements();
      } else {
        setKey('error', displayMessage);
      }
    } finally {
      dispatch({ type: 'SET_KEY', key: 'isLoading', value: false });
      isGeneratingRef.current = false;
      generationStartTimeRef.current = null;
      taskIdRef.current = null;
      // Clear stepper after a delay so the user can see final state
      setTimeout(() => setKey('pipelineProgress', null), 3000);
    }
  }, [dispatch, setKey, stateRef, performCancel]);

  const handleGenerate = useCallback(async () => {
    if (isGeneratingRef.current) return;
    const s = stateRef.current;
    if (!s.taskText || s.taskText.trim().length < 10) {
      setKey('error', i18n.t('errors:generation.taskTooShort'));
      return;
    }
    await executeGeneration(s);
  }, [executeGeneration, stateRef, setKey]);

  const regenerateFromEntry = useCallback(async (entry) => {
    if (isGeneratingRef.current || !entry.params) return;
    // entry.params has wire shape (tone_hint / legacy tone_overrides) that
    // does not match state field names (tone/customTone). toneStateFromParams
    // inverts it so buildBriefRequest re-reads the same tone the entry used.
    const toneState = toneStateFromParams(entry.params);
    await executeGeneration({ ...stateRef.current, ...entry.params, ...toneState });
  }, [executeGeneration, stateRef]);

  const cancelGeneration = useCallback(() => {
    if (!isGeneratingRef.current) return;
    if (cancelInFlightRef.current) return;
    if (cancelStatusRef.current === 'pending' || cancelStatusRef.current === 'too-late') return;
    if (!taskIdRef.current) { cancelRequestedRef.current = true; return; }
    performCancel(taskIdRef.current);
  }, [performCancel]);

  return { handleGenerate, regenerateFromEntry, cancelGeneration, isGeneratingRef, generationStartTimeRef };
}
